// src/pages/Region/AddRegion.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import regionService from '../../services/region.service';
import companyService from '../../services/company.service';
import '../../styles/AddPages.css';

const AddRegion = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    companyId: '',
  });

  const [companies, setCompanies] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCompanies();
    } else {
      // Set user's company
      const userCompanyId = user?.companyId || '';
      setFormData(prev => ({ ...prev, companyId: userCompanyId }));
    }
  }, [isSuperAdmin, user]);

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies();
      if (response.success && response.data) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Region name is required';
    }

    if (isSuperAdmin && !formData.companyId) {
      newErrors.companyId = 'Company is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
      };

      if (isSuperAdmin) {
        payload.companyId = parseInt(formData.companyId);
      }

      const response = await regionService.createRegion(payload);

      if (response.success) {
        alert('Region added successfully!');
        navigate('/region/view');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add region');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-region-page">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard/overview">Dashboard</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/region/view">Regions</a>
          </li>
          <li className="breadcrumb-item active">Add Region</li>
        </ol>
      </nav>

      {/* Page Header */}
      <div className="page-header">
        <h4 className="page-title">Add New Region</h4>
      </div>

      {/* Form Card */}
      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {/* Region Information */}
          <div className="form-section">
            
            <div className="form-row">
              {isSuperAdmin && (
                <div className="form-group">
                  <label className="form-label">
                    Select Company <span className="required">*</span>
                  </label>
                  <select
                    name="companyId"
                    className={`form-input ${errors.companyId ? 'error' : ''}`}
                    value={formData.companyId}
                    onChange={handleChange}
                  >
                    <option value="">Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  {errors.companyId && (
                    <span className="error-message">{errors.companyId}</span>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  Region Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter region name (e.g., North Zone)"
                />
                {errors.name && (
                  <span className="error-message">{errors.name}</span>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/region/view')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding Region...' : 'Add Region'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRegion;